<template>
  <div class="server-scheduled-tab">
    <div class="server-scheduled-tab__card">
      <div class="server-scheduled-tab__card-head">
        <h4>Scheduled tasks</h4>
        <div class="server-scheduled-tab__head-right">
          <span v-if="collectedAt" class="server-scheduled-tab__fetched">fetched {{ formatRelativeTime(collectedAt) }}</span>
          <button type="button" class="server-scheduled-tab__btn" :disabled="refreshing" @click="refresh">
            {{ refreshing ? 'Refreshing…' : 'Refresh' }}
          </button>
        </div>
      </div>
      <p v-if="error" class="server-scheduled-tab__error">{{ error }}</p>
      <div v-if="loaded && !data" class="server-scheduled-tab__empty">
        No scheduled-task data reported yet — refresh to ask the agent (requires agent v3.0.23-staging.11+).
      </div>
    </div>

    <template v-if="data">
      <div class="server-scheduled-tab__card">
        <div class="server-scheduled-tab__card-head"><h4>Per-user crontabs</h4></div>
        <div v-for="userBlock in users" :key="userBlock.user" class="server-scheduled-tab__section">
          <div class="server-scheduled-tab__section-title">{{ userBlock.user }}</div>
          <table>
            <thead><tr><th class="server-scheduled-tab__col-schedule">Schedule</th><th>Command</th></tr></thead>
            <tbody>
              <tr v-for="(entry, i) in userBlock.entries" :key="i">
                <td class="server-scheduled-tab__mono">{{ entry.schedule }}</td>
                <td class="server-scheduled-tab__mono">{{ entry.command }}</td>
              </tr>
              <tr v-if="!userBlock.entries.length">
                <td colspan="2" class="server-scheduled-tab__empty">No entries.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="!users.length" class="server-scheduled-tab__empty">No user crontabs found.</div>
      </div>

      <div class="server-scheduled-tab__card">
        <div class="server-scheduled-tab__card-head"><h4>System cron files</h4></div>
        <div v-for="fileBlock in systemFiles" :key="fileBlock.file" class="server-scheduled-tab__section">
          <div class="server-scheduled-tab__section-title server-scheduled-tab__mono">{{ fileBlock.file }}</div>
          <table>
            <thead><tr><th class="server-scheduled-tab__col-schedule">Schedule</th><th class="server-scheduled-tab__col-user">User</th><th>Command</th></tr></thead>
            <tbody>
              <tr v-for="(entry, i) in fileBlock.entries" :key="i">
                <td class="server-scheduled-tab__mono">{{ entry.schedule }}</td>
                <td>{{ entry.user }}</td>
                <td class="server-scheduled-tab__mono">{{ entry.command }}</td>
              </tr>
              <tr v-if="!fileBlock.entries.length">
                <td colspan="3" class="server-scheduled-tab__empty">No entries.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="!systemFiles.length" class="server-scheduled-tab__empty">No system cron files found.</div>
      </div>

      <div class="server-scheduled-tab__card">
        <div class="server-scheduled-tab__card-head"><h4>Systemd timers</h4></div>
        <table>
          <thead><tr><th>Unit</th><th>Next run</th><th>Last run</th><th>Activates</th></tr></thead>
          <tbody>
            <tr v-for="timer in timers" :key="timer.unit">
              <td class="server-scheduled-tab__mono">{{ timer.unit }}</td>
              <td>{{ timer.next_run || '—' }}</td>
              <td>{{ timer.last_run || '—' }}</td>
              <td class="server-scheduled-tab__mono">{{ timer.activates || '—' }}</td>
            </tr>
            <tr v-if="!timers.length">
              <td colspan="4" class="server-scheduled-tab__empty">No systemd timers found.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import useIntrospection from '@root/hooks/useIntrospection';
import { formatRelativeTime } from '@root/services/serverFormat';

// `server-detail.html`'s Scheduled tasks tab — renders the agent's
// `crontab.list` result (per-user crontabs, system cron files, systemd
// timers). Last-known data loads instantly from the persisted introspection
// row; Refresh re-issues the command via the shared useIntrospection hook.
const props = defineProps({
  apiBase: { type: String, required: true },
  serverId: { type: [Number, String], required: true },
});

const { data, collectedAt, loaded, refreshing, error, load, refresh } = useIntrospection(props.apiBase, props.serverId, 'crontab.list');

const users = computed(() => (Array.isArray(data.value?.users) ? data.value.users : []));
const systemFiles = computed(() => (Array.isArray(data.value?.system) ? data.value.system : []));
const timers = computed(() => (Array.isArray(data.value?.timers) ? data.value.timers : []));

onMounted(load);
</script>

<style lang="scss">
.server-scheduled-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__card {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    overflow: hidden;
  }

  &__card-head {
    padding: 14px 18px 10px;
    border-bottom: 1px solid var(--theme-border-soft);
    display: flex;
    justify-content: space-between;
    align-items: center;

    h4 {
      margin: 0;
      font-size: 13.5px;
      font-weight: 600;
    }
  }

  &__head-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__fetched {
    font-size: 11px;
    color: var(--theme-text-tertiary);
  }

  &__btn {
    font-size: 11.5px;
    font-weight: 600;
    padding: 5px 12px;
    border-radius: var(--theme-radius, 6px);
    cursor: pointer;
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);

    &:disabled {
      opacity: .6;
      cursor: default;
    }
  }

  &__error {
    color: #a53232;
    font-size: 12px;
    padding: 10px 18px;
    margin: 0;
  }

  &__section {
    border-top: 1px solid var(--theme-border-soft);

    &:first-of-type {
      border-top: none;
    }
  }

  &__section-title {
    padding: 10px 18px 4px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .03em;
    color: var(--theme-text-secondary);
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th {
    text-align: left;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .05em;
    text-transform: uppercase;
    color: var(--theme-text-tertiary);
    padding: 8px 18px;
    border-bottom: 1px solid var(--theme-border-soft);
  }

  td {
    padding: 8px 18px;
    font-size: 12.5px;
    border-top: 1px solid var(--theme-border-soft);
    vertical-align: top;
    word-break: break-word;
  }

  &__col-schedule { width: 190px; }
  &__col-user { width: 110px; }

  &__mono {
    font-family: var(--theme-font-mono);
    font-size: 11.5px;
  }

  &__empty {
    color: var(--theme-text-tertiary);
    padding: 16px 18px;
    text-align: center;
    font-size: 12.5px;
  }
}
</style>
